import { useEffect, useMemo, useState } from "react";
import { ReactComponent as Vector9Icon } from "../../assets/images/vector_arrow.svg";
import {
  MailSummaryCardRow,
  MailItemCardRow,
  getMailSummaryColumns,
  getMailItemColumns,
  MAIL_BRAND_COLUMN_DEFAULT,
} from "../../ui/cards/MailCards";
import { measureTextWidth } from "../../utils/measureTextWidth";
import BooleanStatusIcon from "../../ui/icons/BooleanStatusIcon";
import styles from "../../styles/components/MailsTable.module.css";

const BRAND_TAG_FONT = '500 13px "DM Sans", sans-serif';
const BRAND_TAG_HORIZONTAL_PADDING = 24;
const BRAND_TAG_BUFFER = 4;
const BRAND_COLUMN_MIN = 96;
const BRAND_COLUMN_MAX = 240;

function computeBrandColumnWidth(brandNames) {
  const measurableNames = brandNames.filter((name) => name && name.trim());
  if (!measurableNames.length) return MAIL_BRAND_COLUMN_DEFAULT;

  const widestTextWidth = measurableNames.reduce(
    (widest, name) => Math.max(widest, measureTextWidth(name, BRAND_TAG_FONT)),
    0,
  );

  // Sin una medición real (p.ej. canvas no disponible), usar el ancho por
  // defecto en vez de colapsar a BRAND_COLUMN_MIN.
  if (widestTextWidth === 0) return MAIL_BRAND_COLUMN_DEFAULT;

  const tagWidth =
    Math.ceil(widestTextWidth) + BRAND_TAG_HORIZONTAL_PADDING + BRAND_TAG_BUFFER;

  return Math.min(BRAND_COLUMN_MAX, Math.max(BRAND_COLUMN_MIN, tagWidth));
}

function getStatus(row) {
  if ((row?.late ?? 0) > 15) return "late";
  return "uptodate";
}

function getStatusText(row) {
  const late = row?.late ?? 0;
  return late > 15 ? `${late}% late` : "Up to date";
}

function getPercentageText(row) {
  if (row?.response_rate == null || row?.response_rate === "") return "-";
  return `${row.response_rate}%`;
}

function formatLastSend(dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function getMailItemOwnerName(mailItem) {
  return mailItem?.responsible?.name?.trim() || "-";
}

function MailItemStatusIcon({ mailItem }) {
  const warningReason = mailItem?.rejected
    ? "Rechazado"
    : mailItem?.send_error || null;

  if (warningReason) {
    return (
      <span
        role="img"
        aria-label="Advertencia"
        title={warningReason}
        style={{ fontSize: 16, cursor: "default" }}
      >
        ⚠️
      </span>
    );
  }

  return (
    <BooleanStatusIcon
      value={mailItem?.status}
      trueLabel="Activo"
      falseLabel="Inactivo"
    />
  );
}

function HeaderCell({ children, column }) {
  return (
    <div
      style={{
        gridColumn: column,
        textAlign: "center",
        color: "var(--color-text-text-primary)",
        fontSize: 14,
        fontFamily: "DM Sans",
        fontWeight: 500,
        lineHeight: "17px",
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {children}
    </div>
  );
}

function MailsTable({
  rows = [],
  onCardSelected,
  selectedMails = [],
  selectComunication,
  headers = [],
  mailComunication,
  mailItems = [],
  handleLateralModal,
}) {
  const brandNames = useMemo(() => {
    const source = mailComunication ? mailItems : rows;
    return source.map((entry) =>
      mailComunication
        ? entry?.mail_account?.name ?? ""
        : entry?.account?.name ?? "",
    );
  }, [mailComunication, mailItems, rows]);

  const [brandColumnWidth, setBrandColumnWidth] = useState(
    MAIL_BRAND_COLUMN_DEFAULT,
  );
  useEffect(() => {
    let isActive = true;

    const updateBrandColumnWidth = () => {
      if (isActive) setBrandColumnWidth(computeBrandColumnWidth(brandNames));
    };

    updateBrandColumnWidth();
    document.fonts?.ready?.then(updateBrandColumnWidth);

    return () => {
      isActive = false;
    };
  }, [brandNames]);

  const summaryColumns = useMemo(
    () => getMailSummaryColumns(brandColumnWidth),
    [brandColumnWidth],
  );
  const itemColumns = useMemo(
    () => getMailItemColumns(brandColumnWidth),
    [brandColumnWidth],
  );

  return (
    <div className={styles.mailsTableContainer}>
      <div className={styles.mailsTable}>
        <div className={styles.headerContainer}>
          <div className={styles.headerBody} style={mailComunication ? { padding: "0 24px" } : undefined}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: mailComunication ? itemColumns : summaryColumns,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              {headers.map((header) => (
                <HeaderCell key={header.column} column={header.column}>
                  {header.label}
                </HeaderCell>
              ))}
            </div>
          </div>

          <div className={styles.headerActionSpacer} />
        </div>

        {mailComunication
          ? mailItems?.map((mailItem) => {
              const isSelected = selectedMails.some(
                (mail) => mail.id === mailItem.id,
              );

              return (
                <div
                  key={`MailCard${mailItem.id}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onCardSelected?.(mailItem);
                    }
                  }}
                  className={styles.rowWrapper}
                  style={{
                    outline: isSelected
                      ? "2px dashed var(--color-brand-500)"
                      : "none",
                  }}
                >
                  <MailItemCardRow
                    columns={itemColumns}
                    brand={mailItem?.mail_account?.name ?? "-"}
                    brandColor={mailItem?.mail_account?.color}
                    step={mailItem?.send_number ?? "-"}
                    campaign={mailItem?.lead?.name ?? "-"}
                    subject={mailItem?.subject}
                    activeStatus={<MailItemStatusIcon mailItem={mailItem} />}
                    responseStatus={
                      <BooleanStatusIcon
                        value={mailItem?.status_response}
                        trueLabel="Respondido"
                        falseLabel="Sin respuesta"
                      />
                    }
                    datetime={formatLastSend(mailItem?.last_send)}
                    onAction={() => onCardSelected?.(mailItem)}
                    iconOnAction={() => handleLateralModal?.(mailItem)}
                    ownerName={getMailItemOwnerName(mailItem)}
                    actionIcon={<Vector9Icon />}
                  />
                </div>
              );
            })
          : rows.map((row) => {
              const isSelected = selectedMails.some(
                (mail) => mail.id === row.id,
              );

              return (
                <div
                  key={`MailCard${row.id}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onCardSelected?.(row);
                    }
                  }}
                  className={styles.rowWrapper}
                  style={{
                    outline: isSelected
                      ? "2px dashed var(--color-brand-500)"
                      : "none",
                  }}
                >
                  <MailSummaryCardRow
                    columns={summaryColumns}
                    status={getStatus(row)}
                    brand={row?.account?.name ?? "-"}
                    brandColor={row?.account?.color}
                    segment={row?.group?.name ?? "-"}
                    count={String(row?.mails_count ?? 0)}
                    statusText={getStatusText(row)}
                    percentage={getPercentageText(row)}
                    onAction={() => onCardSelected?.(row)}
                    actionIcon={<Vector9Icon />}
                    iconOnAction={() => selectComunication?.(row.id)}
                  />
                </div>
              );
            })}
      </div>
    </div>
  );
}

export default MailsTable;
